import dayjs, { Dayjs } from 'dayjs';
import { AxiosError } from 'axios';
import type {
  CandidatoSemana, DatosFichaF4, ElementoBandeja, EncuestaF4, EquipoOperacion, ResultadoAprobar, TipoBandeja,
  TipoCompra,
} from '../../api/cxPanelApi';
import type { FctFamilia, FctNombreIcono } from '../../fct/react';
import { fmtFecha, fmtFechaHora, fmtNumero } from '../../fct/react/formato';

/**
 * Lógica pura del panel «Encuestas CX»: fechas de la semana, enmascarado de email, contadores y
 * presentación de tipos. Separada de los componentes para poder probarla sin montar nada.
 */

/** Tipos compradores en orden de prioridad (ROC > OC > SI). */
export const TIPOS_COMPRA: TipoCompra[] = ['ROC', 'OC', 'SI'];

/** Filtro de tipo de la cabecera del panel: un tipo concreto o todos. */
export const FILTRO_TODOS = 'TODOS';
export type FiltroTipo = typeof FILTRO_TODOS | 'ROC' | 'OC' | 'SI' | 'OV' | 'F2' | 'F0' | 'CIV' | 'F4' | 'OTRA'
  | 'V0' | 'V1' | 'V2' | 'F4O';

/** Orden de los tipos en el filtro de la vista «Registradas»; solo se pintan los presentes. */
export const ORDEN_TIPOS_REGISTRADAS = ['ROC', 'OC', 'SI', 'OV', 'F2', 'F0', 'CIV', 'F4', 'OTRA'];

/** Color (hex) del contador de cada tipo en el filtro; mismo tono que su Tag. */
export const TIPO_COLOR_HEX: Record<string, string> = {
  ROC: '#389e0d',
  OC: '#0958d9',
  SI: '#d48806',
  OV: '#08979c',
  F2: '#531dab',
  F0: '#d4380d',
  CIV: '#1d39c4',
  F4: '#c41d7f',
  OTRA: '#8c8c8c',
};

/**
 * Enlace al informe de la campaña en la web de Brevo (patrón comprobado el 2026-10-02 con la
 * campaña 9819). Si Brevo lo cambia, se cambia solo aquí.
 */
export const urlCampanaBrevo = (id: number): string => `https://app.brevo.com/marketing-reports/email/${id}/overview`;

/**
 * Enlace al Portal Mystery del área de clientes para el personal de CxCp y Sistemas: entra con su
 * cuenta corporativa (SSO), sin botón en la pantalla del portal; el parámetro `acceso=fct` es lo que
 * le dice al portal que use el login corporativo. El entorno se deduce del host de GescomAux:
 * local → el portal local; STG → el de STG; el resto → producción.
 */
export const urlPortalMystery = (host: string = window.location.hostname): string => {
  const h = host.toLowerCase();
  const base = h === 'localhost' || h === '127.0.0.1'
    ? 'http://localhost:5173'
    : h.includes('stg')
      ? 'https://areaclientes-stg.farmaconsulting.es'
      : 'https://areaclientes.farmaconsulting.es';
  return `${base}/encuestas?acceso=fct`;
};

/** Cuántas filas hay de cada tipo (para los contadores del filtro). */
export const contarTipos = (filas: { tipo: string }[]): Record<string, number> =>
  filas.reduce<Record<string, number>>((acc, f) => {
    acc[f.tipo] = (acc[f.tipo] ?? 0) + 1;
    return acc;
  }, {});

/** ¿Pasa la fila el filtro de tipo de la cabecera? */
export const coincideTipo = (filtro: FiltroTipo, tipo: string): boolean =>
  filtro === FILTRO_TODOS || filtro === tipo;

/** Color AntD del Tag de cada tipo. Los de la campaña compradora, distinguibles entre sí. */
export const TIPO_COLOR: Record<string, string> = {
  ROC: 'green',
  OC: 'blue',
  SI: 'gold',
  OV: 'cyan',
  F2: 'purple',
  F0: 'volcano',
  CIV: 'geekblue',
  F4: 'magenta',
  OTRA: 'default',
};

/** Descripción corta de cada tipo, para el tooltip del Tag. */
export const TIPO_DESCRIPCION: Record<string, string> = {
  ROC: 'ROC: actividad con protocolo ROC dada de alta en la semana',
  OC: 'Oportunidad de compra entrada por la web',
  SI: 'Solicitud de información',
  OV: 'Oportunidad de venta',
  F2: 'Seguimiento de operación en Fase 2 (vendedor)',
  F0: 'Fase 0: cartera de captación',
  CIV: 'Encuesta CIV',
  F4: 'Encuesta F4 (gestión telefónica de Mystery)',
  OTRA: 'Otra encuesta (histórica o sin tipo reconocido)',
};

/** Lunes (00:00) de la semana a la que pertenece `fecha`. Sin plugins: lunes = día 1 de dayjs. */
export const lunesDe = (fecha: Dayjs): Dayjs => {
  const desplazamiento = (fecha.day() + 6) % 7; // domingo (0) → 6, lunes (1) → 0
  return fecha.subtract(desplazamiento, 'day').startOf('day');
};

/** Lunes de la semana ANTERIOR a `hoy`: la que procesa la campaña del jueves de esta semana. */
export const lunesSemanaAnterior = (hoy: Dayjs = dayjs()): Dayjs => lunesDe(hoy).subtract(7, 'day');

/** Jueves en que saldría la campaña de la semana que empieza en `lunes` (jueves de la siguiente). */
export const juevesDeEnvio = (lunes: Dayjs): Dayjs => lunesDe(lunes).add(10, 'day');

/** Texto de la semana para el selector: «Semana del 21/09 al 27/09/2026». */
export const etiquetaSemana = (fecha: Dayjs): string => {
  const lunes = lunesDe(fecha);
  return `Semana del ${lunes.format('DD/MM')} al ${lunes.add(6, 'day').format('DD/MM/YYYY')}`;
};

/** Texto corto de la semana para el selector: «21/09 – 27/09/2026». */
export const rangoSemana = (fecha: Dayjs): string => {
  const lunes = lunesDe(fecha);
  return `${lunes.format('DD/MM')} – ${lunes.add(6, 'day').format('DD/MM/YYYY')}`;
};

/** Formato de fecha que viaja a la API (día natural). */
export const aIsoDia = (fecha: Dayjs): string => fecha.format('YYYY-MM-DD');

/** Texto normalizado para buscar: sin tildes, en minúsculas y sin espacios en los extremos. */
export const normalizar = (texto: string): string =>
  texto.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();

/**
 * ¿Casa la búsqueda con alguno de los campos? Sin distinguir tildes ni mayúsculas; una búsqueda
 * vacía casa con todo. Sirve para «¿se le ha enviado algo a este cliente?» por nombre, código «CL…»,
 * email o CCL.
 */
export const coincideBusqueda = (busqueda: string, ...campos: Array<string | number | null | undefined>): boolean => {
  const q = normalizar(busqueda);
  if (!q) return true;
  return campos.some((c) => c != null && normalizar(String(c)).includes(q));
};

/** Orden de la tabla de la semana: por prioridad de tipo y, dentro, por nombre. */
export const compararCandidatos = (
  a: Pick<CandidatoSemana, 'tipo' | 'nombre' | 'codigoCliente'>,
  b: Pick<CandidatoSemana, 'tipo' | 'nombre' | 'codigoCliente'>,
): number => {
  const pa = TIPOS_COMPRA.indexOf(a.tipo);
  const pb = TIPOS_COMPRA.indexOf(b.tipo);
  if (pa !== pb) return pa - pb;
  return (a.nombre ?? a.codigoCliente ?? '').localeCompare(b.nombre ?? b.codigoCliente ?? '', 'es');
};

/**
 * Mensaje legible de un fallo de la API del panel. Un 404 en las rutas `panel/*` significa, mientras
 * se construye, que el backend desplegado aún no las tiene; un 403, que falta el recurso en la ACL.
 */
export const mensajeError = (err: unknown, queCargaba: string): string => {
  const status = err instanceof AxiosError ? err.response?.status : undefined;
  if (status === 403) return `No tienes permiso para ver ${queCargaba}.`;
  if (status === 404) return `El backend no expone todavía ${queCargaba} (404).`;
  if (status === 400) {
    const detalle = err instanceof AxiosError
      ? (err.response?.data as { detalle?: string; error?: string } | undefined)
      : undefined;
    return `Petición no válida al cargar ${queCargaba}${detalle?.detalle || detalle?.error ? `: ${detalle.detalle ?? detalle.error}` : '.'}`;
  }
  return `No se pudo cargar ${queCargaba}. Vuelve a intentarlo.`;
};

/** Prefijos de tipo de encuesta que llevan los códigos de pregunta (en mayúsculas: «Si» no es «SI»). */
const PREFIJOS_PREGUNTA = /^(ROC|OC|SI|OV|OP|CIV|F\d+)$/;

/**
 * Pregunta legible a partir de su código (`GC_PREGUNTA.DS_PREGUNTA`, p. ej. `F4_NPS_Recomendacion`):
 * separa por `_`, quita el prefijo del tipo de encuesta (`ROC_`, `OP_`, `F4_`…) y separa las
 * palabras pegadas. Las siglas (NPS) se respetan. `F4_NPS_Recomendacion` → «NPS · Recomendacion».
 */
export const preguntaLegible = (codigo: string): string => {
  const partes = codigo.split('_').map((p) => p.trim()).filter(Boolean);
  // Prefijo del tipo de encuesta (ROC, OC, SI, OV, OP, CIV, F0, F2, F4…), si hay más detrás.
  if (partes.length > 1 && PREFIJOS_PREGUNTA.test(partes[0])) partes.shift();
  if (partes.length === 0) return codigo;
  const palabras = partes.map((p) => {
    // «MomentosClave» → «Momentos clave»; las siglas (NPS) se quedan en mayúsculas.
    const texto = p.replace(/([a-záéíóúñü0-9])([A-ZÁÉÍÓÚÑ])/g, '$1 $2')
      .split(' ')
      .map((w) => (/^[A-Z0-9]{2,}$/.test(w) ? w : w.toLowerCase()))
      .join(' ');
    return texto.charAt(0).toUpperCase() + texto.slice(1);
  });
  return palabras.join(' · ');
};

/** ¿Es claramente una pregunta de sí/no? Solo si su código lo dice (`…SiNo…` / `…Si_No…`). */
/**
 * Preguntas de sí/no o casilla que Gescom guarda como '1'/'0' (mismo criterio que el portal de
 * Mystery y el registro automático desde Typeform): F4 «preocupación», «satisfecho con el precio»,
 * los motivos del NPS y los aspectos a mejorar (salvo los de texto libre).
 */
const PREGUNTAS_SI_NO = /^(F4_Palabras_[A-Za-z]+|F4_MomentosClave_Preocupacion|F4_Valor_SatisfechoPrecio|F4_NPS_(Cercania|Claridad|Disponibilidad|Equipo|Eficacia|Seguridad)|F4_Mejora_(Comunicacion|Claridad|Agilidad|Coordinacion|Informacion|Ninguna))$/;

export const esPreguntaSiNo = (codigo: string): boolean => PREGUNTAS_SI_NO.test(codigo) || /si_?no/i.test(codigo);

/**
 * Número de una respuesta: Gescom guarda texto y el sistema anterior dejó decimales de relleno
 * («10.0000000000»). Devuelve el número (con coma o punto decimal) o null si no lo es.
 */
export const numeroRespuesta = (valor?: string | null): number | null => {
  const v = (valor ?? '').trim();
  // Sin ceros a la izquierda: «08001» (un código postal, un teléfono…) no es un número que normalizar.
  if (!/^-?(0|[1-9]\d*)([.,]\d+)?$/.test(v)) return null;
  const n = Number(v.replace(',', '.'));
  return Number.isFinite(n) ? n : null;
};

/** Número para leer: sin decimales de relleno («10.0000000000» → «10»; «4.50» → «4,5»). */
export const numeroLegible = (n: number): string =>
  (Number.isInteger(n) ? String(n) : fmtNumero(n, 2).replace(/,?0+$/, ''));

/**
 * Respuesta para mostrar: `true`/`false` siempre como Sí/No; `1`/`0` solo si la pregunta es
 * claramente de sí/no (en las de puntuación, 0 y 1 son notas). Los números, sin decimales de
 * relleno. El resto, tal cual.
 */
export const respuestaLegible = (codigo: string, respuesta?: string | null): string | null => {
  if (respuesta == null) return null;
  const r = respuesta.trim();
  if (r === '') return null;
  const minus = r.toLowerCase();
  if (minus === 'true') return 'Sí';
  if (minus === 'false') return 'No';
  if (esPreguntaSiNo(codigo) && (r === '1' || r === '0')) return r === '1' ? 'Sí' : 'No';
  const n = numeroRespuesta(r);
  if (n != null) return numeroLegible(n);
  return respuesta;
};

/**
 * ¿Es la pregunta de recomendación, es decir, el NPS? (`ROC_Recomendacion`, `OP_Recomendacion`,
 * `F2_Recomendacion`…). La de F4 (`F4_NPS_Recomendacion`) la pinta su propio cuestionario.
 */
export const esPreguntaNps = (codigo: string): boolean => /^([A-Z0-9]+_)?Recomendacion$/i.test(codigo.trim());

/** Categoría del NPS según la nota (0-10): 9-10 promotor, 7-8 pasivo, 0-6 detractor. */
export interface NotaNps { nota: number; categoria: 'Promotor' | 'Pasivo' | 'Detractor'; familia: FctFamilia; icono: FctNombreIcono }

/**
 * Nota del NPS con su familia del semáforo del manual (D-GCVSC-023), para pintarla con FctEstado:
 * 9-10 éxito (verde), 7-8 aviso (amarillo), 0-6 riesgo (rojo). Null si no es una nota de 0 a 10.
 */
export const notaNps = (valor?: string | null): NotaNps | null => {
  const n = numeroRespuesta(valor);
  if (n == null || n < 0 || n > 10) return null;
  const nota = Math.round(n);
  if (nota >= 9) return { nota, categoria: 'Promotor', familia: 'exito', icono: 'completado' };
  if (nota >= 7) return { nota, categoria: 'Pasivo', familia: 'aviso', icono: 'punto' };
  return { nota, categoria: 'Detractor', familia: 'riesgo', icono: 'alerta' };
};

/**
 * Atajos del selector de fechas (un clic en vez de dos fechas a mano). Comunes a «Registradas» y al
 * histórico de «Pendientes»; se calculan al pintar para que «hoy» sea siempre hoy.
 */
export const presetsRango = (hoy: Dayjs = dayjs()): { label: string; value: [Dayjs, Dayjs] }[] => [
  { label: 'Esta semana', value: [lunesDe(hoy), hoy] },
  { label: 'Semana pasada', value: [lunesDe(hoy).subtract(7, 'day'), lunesDe(hoy).subtract(1, 'day')] },
  { label: 'Este mes', value: [hoy.startOf('month'), hoy] },
  { label: 'Mes pasado', value: [hoy.subtract(1, 'month').startOf('month'), hoy.subtract(1, 'month').endOf('month')] },
  { label: 'Últimos 30 días', value: [hoy.subtract(30, 'day'), hoy] },
  { label: 'Últimos 90 días', value: [hoy.subtract(90, 'day'), hoy] },
  { label: 'Último año', value: [hoy.subtract(365, 'day'), hoy] },
];

// ---------------------------------------------------------------------------------------------
// Bandeja «Pendientes de envío» (contrato §8, D-CX-68)
// ---------------------------------------------------------------------------------------------

/** Recurso ACL que permite aprobar y descartar (el backend lo exige en `bandeja/aprobar|descartar`). */
export const RECURSO_BANDEJA_WRITE = 'Cx.Bandeja.Write';

/**
 * Tipos de la bandeja, en el orden del proceso de venta. Las bienvenidas (V0 y V1) se retiraron de Encuestas CX
 * (D-CX-84): las envía otro proceso. Sus nombres siguen abajo para el histórico de «Enviados y descartados».
 */
export const TIPOS_BANDEJA: TipoBandeja[] = ['V2', 'F2', 'F4O'];

/** Nombre corto de cada tipo, el que se ve en el filtro y en la tabla (el código V0, V1… no dice nada a CxCp). */
export const TIPO_BANDEJA_NOMBRE: Record<string, string> = {
  V0: 'Bienvenida oportunidad',
  V1: 'Bienvenida Fase 1',
  V2: 'Área privada',
  F2: 'Encuesta F2',
  F4O: 'F4 online',
};

/** Qué es cada tipo, en palabras de CxCp (tooltip de la etiqueta y del filtro). */
export const TIPO_BANDEJA_DESCRIPCION: Record<string, string> = {
  V0: 'V0 · Bienvenida al dar de alta la oportunidad de venta',
  V1: 'V1 · Bienvenida al cliente cuando la operación entra en Fase 1',
  V2: 'V2 · Acceso al área privada cuando la operación entra en Fase 1',
  F2: 'F2 · Encuesta de Fase 2: a los 15, 45, 90, 135… días del pase a Fase 2',
  F4O: 'F4 online · Encuesta F4 por correo cuando Mystery no consigue contactar en 6 intentos',
};

/** Estado de un elemento de la bandeja en el semáforo del manual (familia + icono + etiqueta). */
export interface EstadoVisible {
  etiqueta: string;
  familia: FctFamilia;
  icono?: FctNombreIcono;
}

const ESTADOS_BANDEJA: Record<string, EstadoVisible> = {
  PENDIENTE: { etiqueta: 'Pendiente', familia: 'aviso', icono: 'hora' },
  BLOQUEADO: { etiqueta: 'Bloqueado', familia: 'riesgo', icono: 'alerta' },
  APROBADO: { etiqueta: 'Aprobado · sale en el próximo envío', familia: 'proceso', icono: 'transmision' },
  ENVIADO: { etiqueta: 'Enviado', familia: 'exito', icono: 'completado' },
  DESCARTADO: { etiqueta: 'Descartado', familia: 'neutro', icono: 'cancelado' },
};

/** Estado para pintar con FctEstado. «Enviado» lleva su fecha; un estado desconocido sale neutro, tal cual. */
export const estadoBandeja = (fila: Pick<ElementoBandeja, 'estado' | 'enviadoEl'>): EstadoVisible => {
  const e = ESTADOS_BANDEJA[fila.estado];
  if (!e) return { etiqueta: fila.estado || 'Sin estado', familia: 'neutro' };
  if (fila.estado === 'ENVIADO' && fila.enviadoEl) return { ...e, etiqueta: `Enviado ${fmtFecha(fila.enviadoEl)}` };
  return e;
};

/** ¿Se puede marcar la fila para aprobarla? Solo lo pendiente: lo bloqueado no se aprueba (le falta algo). */
export const esSeleccionable = (fila: Pick<ElementoBandeja, 'estado'>): boolean => fila.estado === 'PENDIENTE';

/** ¿Sigue abierta (se puede descartar todavía)? Lo pendiente, lo bloqueado y lo aprobado que aún no ha salido. */
const ESTADOS_ABIERTOS = new Set(['PENDIENTE', 'BLOQUEADO', 'APROBADO']);
export const esAbierta = (fila: Pick<ElementoBandeja, 'estado'>): boolean => ESTADOS_ABIERTOS.has(fila.estado);

/**
 * Cuántos envíos siguen abiertos: el contador de la vista. Cuenta lo mismo que devuelve `estado=abiertas`
 * (pendientes, bloqueados y aprobados sin enviar).
 */
export const contarAbiertas = (filas: Pick<ElementoBandeja, 'estado'>[]): number => filas.filter(esAbierta).length;

/** Cuántos pendientes (aprobables) hay de un tipo: los que aprobaría «Aprobar todos los de tipo X». */
export const pendientesDeTipo = (filas: Pick<ElementoBandeja, 'estado' | 'tipo'>[], tipo: string): number =>
  filas.filter((f) => f.tipo === tipo && esSeleccionable(f)).length;

/** Ciclo de la F2 en días («45 días»); en el resto de tipos no aplica (null). */
export const cicloLegible = (fila: Pick<ElementoBandeja, 'tipo' | 'ciclo'>): string | null =>
  (fila.tipo === 'F2' && fila.ciclo != null ? `${fmtNumero(fila.ciclo)} días` : null);

/** Motivo para leer: lo que descartó el sistema solo llega como «AUTO: …» y se enseña como «Automático: …». */
export const motivoLegible = (motivo?: string | null): string | null => {
  const m = motivo?.trim();
  if (!m) return null;
  return m.replace(/^AUTO:\s*/i, 'Automático: ');
};

/** Equipo de la operación en una línea, con «—» en lo que falta (para saber a quién avisar de un bloqueo). */
export const equipoLegible = (equipo?: EquipoOperacion | null): string | null => {
  if (!equipo) return null;
  const v = (x?: string | null) => (x && x.trim() ? x.trim() : '—');
  return `Asesor PV: ${v(equipo.asesor)} · Analista: ${v(equipo.analista)} · Jurídico: ${v(equipo.juridico)}`;
};

/** Quién y cuándo aprobó («Aprobado por maria.cuenca@… el 07/10/2026 09:41»); el modo automático se dice así. */
export const aprobadoPorLegible = (fila: Pick<ElementoBandeja, 'aprobadoPor' | 'aprobadoEl'>): string | null => {
  if (!fila.aprobadoPor && !fila.aprobadoEl) return null;
  const quien = fila.aprobadoPor === 'automatico' ? 'automáticamente' : fila.aprobadoPor ? `por ${fila.aprobadoPor}` : '';
  const cuando = fila.aprobadoEl ? `el ${fmtFechaHora(fila.aprobadoEl)}` : '';
  return ['Aprobado', quien, cuando].filter(Boolean).join(' ');
};

/** «1 envío» / «3 envíos», con el número en formato español. */
export const plural = (n: number, singular: string, varios: string): string => `${fmtNumero(n)} ${n === 1 ? singular : varios}`;

/** Texto de la confirmación de aprobar: cuántos correos saldrán y, en modo prueba, adónde. */
export const textoConfirmarAprobar = (n: number, modoPrueba: boolean): string => {
  const base = n === 1
    ? 'Se enviará 1 correo en el próximo envío diario.'
    : `Se enviarán ${fmtNumero(n)} correos en el próximo envío diario.`;
  return modoPrueba ? `${base} Modo prueba: saldrán a las direcciones de prueba, no al cliente.` : base;
};

export type TipoMensaje = 'success' | 'warning' | 'info';

/** Mensaje del resultado de aprobar, con lo que no se pudo aprobar y por qué. */
export const mensajeAprobacion = (r: ResultadoAprobar): { tipo: TipoMensaje; texto: string } => {
  const partes: string[] = [];
  if (r.aprobados > 0) {
    partes.push(`${plural(r.aprobados, 'envío aprobado', 'envíos aprobados')}: ${r.aprobados === 1 ? 'saldrá' : 'saldrán'} en el próximo envío.`);
  } else {
    partes.push('No se ha aprobado ningún envío.');
  }
  if (r.bloqueados > 0) {
    partes.push(`${plural(r.bloqueados, 'no se ha podido aprobar', 'no se han podido aprobar')} porque le falta algún dato (por ejemplo, el email del cliente).`);
  }
  if (r.ignorados > 0) {
    partes.push(`${plural(r.ignorados, 'ya no estaba pendiente', 'ya no estaban pendientes')} (aprobado, enviado o descartado mientras tanto).`);
  }
  const limpio = r.bloqueados === 0 && r.ignorados === 0;
  const tipo: TipoMensaje = !limpio ? 'warning' : r.aprobados > 0 ? 'success' : 'info';
  return { tipo, texto: partes.join(' ') };
};

/** Mensaje del resultado de descartar: lo descartado y lo que ya no se podía descartar. */
export const mensajeDescarte = (pedidos: number, descartados: number): { tipo: TipoMensaje; texto: string } => {
  if (descartados === 0) {
    return { tipo: 'warning', texto: 'No se ha descartado ningún envío: ya se habían enviado o descartado.' };
  }
  const texto = `${plural(descartados, 'envío descartado', 'envíos descartados')}: no se ${descartados === 1 ? 'volverá' : 'volverán'} a preparar.`;
  const resto = pedidos - descartados;
  if (resto <= 0) return { tipo: 'success', texto };
  return {
    tipo: 'warning',
    texto: `${texto} ${plural(resto, 'ya no se podía descartar', 'ya no se podían descartar')} (ya enviado o descartado).`,
  };
};

/** Límites del motivo de descarte (los mismos que valida el backend). */
export const MOTIVO_MIN = 3;
export const MOTIVO_MAX = 200;

/** ¿Vale el motivo del descarte? Entre 3 y 200 caracteres sin contar los espacios de los extremos. */
export const motivoValido = (motivo: string): boolean => {
  const m = motivo.trim();
  return m.length >= MOTIVO_MIN && m.length <= MOTIVO_MAX;
};

/** Mensaje legible de un fallo al aprobar o descartar (403 sin permiso, 409 cambiado mientras tanto, 400…). */
export const mensajeErrorAccion = (err: unknown, accion: 'aprobar' | 'descartar'): string => {
  const status = err instanceof AxiosError ? err.response?.status : undefined;
  const codigo = err instanceof AxiosError
    ? (err.response?.data as { error?: string } | undefined)?.error
    : undefined;
  if (status === 403) return `No tienes permiso para ${accion} envíos.`;
  if (status === 409) {
    return 'Algún envío ha cambiado mientras tanto (otra persona lo ha aprobado, enviado o descartado). '
      + 'La lista se ha actualizado: revísala y vuelve a intentarlo.';
  }
  if (status === 401) return 'Tu sesión ha caducado. Vuelve a entrar en la aplicación.';
  if (status === 400) {
    if (codigo === 'motivo_invalido') return `El motivo tiene que tener entre ${MOTIVO_MIN} y ${MOTIVO_MAX} caracteres.`;
    if (codigo === 'ids_exceden_limite') return 'Se pueden tratar como mucho 1.000 envíos a la vez.';
    if (codigo === 'ids_vacio') return `No hay ningún envío marcado para ${accion}.`;
    return `No se ha podido ${accion}: la petición no es válida.`;
  }
  return `No se ha podido ${accion}. Vuelve a intentarlo.`;
};

// ---------------------------------------------------------------------------------------------
// Ficha F4 de CxCp (contrato §9, D-CX-73 a 76): estado, plazo y notas de cada encuesta F4
// ---------------------------------------------------------------------------------------------

/**
 * Estados del portal de Mystery (mismos códigos y nombres que el portal anterior de AuraQuantic y que
 * el portal nuevo), con su familia del semáforo del manual (D-GCVSC-023): icono + etiqueta, sin colores
 * de Ant Design. 955 y 957 se llaman igual, como en los dos portales.
 */
export const ESTADO_MYSTERY: Record<number, { texto: string; familia: FctFamilia }> = {
  953: { texto: 'Pendiente', familia: 'aviso' },
  954: { texto: 'Iniciada', familia: 'proceso' },
  955: { texto: 'Terminada sin respuesta', familia: 'neutro' },
  956: { texto: 'Terminada con respuesta', familia: 'exito' },
  957: { texto: 'Terminada sin respuesta', familia: 'neutro' },
};

/** Estado de una F4 para FctEstado; un código desconocido sale neutro con el número. */
export const estadoMystery = (cod: number): { texto: string; familia: FctFamilia } =>
  ESTADO_MYSTERY[cod] ?? { texto: String(cod), familia: 'neutro' };

/** Máximo de caracteres de las notas FCT (el mismo que valida el backend, D-CX-74). */
export const MAX_NOTAS_FCT = 4000;

const formatoDiaMadrid = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Europe/Madrid', year: 'numeric', month: '2-digit', day: '2-digit',
});

/**
 * Día de Madrid (YYYY-MM-DD) de una fecha ISO del backend. Los plazos son días naturales de Madrid y
 * el backend compara así: reenviar el día que ya tiene no cambia nada, se mire desde donde se mire.
 * Si la fecha no se entiende, se queda con su parte de día (nunca la borra).
 */
export const diaMadrid = (iso?: string | null): string | null => {
  if (!iso) return null;
  const d = new Date(iso);
  if (!Number.isNaN(d.getTime())) return formatoDiaMadrid.format(d);
  return /^\d{4}-\d{2}-\d{2}/.test(iso) ? iso.slice(0, 10) : null;
};

/** ¿Tiene la F4 alguna nota, de FCT o de Mystery? (indicador del botón de la ficha). */
export const tieneNotas = (f: Pick<EncuestaF4, 'notasFct' | 'notasMystery'>): boolean =>
  !!(f.notasFct?.trim() || f.notasMystery?.trim());

/** Recorta un texto a `max` caracteres con «…» (en una sola línea). */
export const recortar = (texto: string, max: number): string => {
  const t = texto.replace(/\s+/g, ' ').trim();
  return t.length <= max ? t : `${t.slice(0, max).trimEnd()}…`;
};

/** Principio de las notas para el tooltip del botón: una línea por cada una que haya. Null si no hay. */
export const resumenNotas = (f: Pick<EncuestaF4, 'notasFct' | 'notasMystery'>, max = 80): string[] | null => {
  const lineas: string[] = [];
  if (f.notasFct?.trim()) lineas.push(`Notas FCT: ${recortar(f.notasFct, max)}`);
  if (f.notasMystery?.trim()) lineas.push(`Notas de Mystery: ${recortar(f.notasMystery, max)}`);
  return lineas.length ? lineas : null;
};

/**
 * Error del plazo para enseñar, o null si vale. Solo se comprueba si el usuario ha cambiado alguna de las
 * fechas: hay F4 antiguas con el fin antes de la creación, y guardar solo las notas no debe fallar.
 * El mismo día de inicio y fin vale. Las fechas van como YYYY-MM-DD (se comparan como texto).
 */
export const errorPlazo = (inicio: string | null, fin: string | null, tocado: boolean): string | null => {
  if (!tocado || !inicio || !fin) return null;
  return fin < inicio ? 'El fin del plazo no puede ser anterior al inicio.' : null;
};

/** Cuerpo del PUT de la ficha: reenvía siempre los tres campos (es un PUT), con las notas en blanco como vacías. */
export const datosFicha = (notas: string, inicio: string | null, fin: string | null): DatosFichaF4 => ({
  notasFct: notas.trim() ? notas.trim() : null,
  plazoInicio: inicio,
  plazoFin: fin,
});

/** Mensaje legible de un fallo al guardar la ficha (400, 403, 404…). */
export const mensajeErrorFicha = (err: unknown): string => {
  const status = err instanceof AxiosError ? err.response?.status : undefined;
  const codigo = err instanceof AxiosError
    ? (err.response?.data as { error?: string } | undefined)?.error
    : undefined;
  if (status === 403) return 'No tienes permiso para cambiar la ficha de las encuestas F4.';
  if (status === 404) {
    return 'Esta encuesta ya no existe o no se puede gestionar desde aquí. Actualiza la lista y vuelve a intentarlo.';
  }
  if (status === 401) return 'Tu sesión ha caducado. Vuelve a entrar en la aplicación.';
  if (status === 400) {
    if (codigo === 'notas_demasiado_largas') return `Las notas FCT no pueden pasar de ${fmtNumero(MAX_NOTAS_FCT)} caracteres.`;
    if (codigo === 'plazo_invalido') return 'El fin del plazo no puede ser anterior al inicio.';
    if (codigo === 'id_invalido') return 'La encuesta no es válida. Actualiza la lista y vuelve a intentarlo.';
    return 'Alguna fecha del plazo no es válida. Revísala y vuelve a intentarlo.';
  }
  return 'No se ha podido guardar la ficha. Vuelve a intentarlo.';
};
